import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sun, RefreshCw, AlertTriangle, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import OrderChip from '../Brand/OrderChip';
import { SkeletonRows } from '../Brand/BrandLoading';
import { ukDate, ukTime } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = {
  waiting: { label: 'Waiting', className: 'bg-cream text-slate-700' },
  arrived: { label: 'Arrived', className: 'bg-amber-100 text-amber-800' },
  late: { label: 'Running late', className: 'bg-red-100 text-red-700' },
  done: { label: 'Done', className: 'bg-green-100 text-green-800' },
};

// Today's jobs with a status chip per person
export const TodayPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/today`, { headers });
      setData(res.data);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    const refresh = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
    /* eslint-disable-next-line */
  }, [token]);

  const clearChecking = async (jobId) => {
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/clear-needs-checking`, {}, { headers });
      toast.success('Cleared');
      load();
    } catch (err) {
      toast.error('Could not clear that');
    }
  };

  return (
    <Card className="bd-card-accent bg-white" data-testid="today-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sun className="h-5 w-5 text-gold-deep" /> Today
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{data ? ukDate(data.date, { withYear: true }) : ''}</span>
            <Button variant="ghost" size="sm" onClick={load} data-testid="today-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {loading && <SkeletonRows rows={2} testId="today-loading" />}

        {!loading && data?.jobs?.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="today-empty">
            No jobs today. Enjoy the quiet.
          </p>
        )}

        {data?.jobs?.map((job) => (
          <div key={job.job_id} className="rounded-[10px] border border-hair p-3"
               data-testid={`today-job-${job.job_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-bold text-ink">{job.job_type}</p>
                  <OrderChip value={job.order_number} fallback="" />
                  {job.time && <span className="text-xs text-slate-500">{ukTime(job.time) || job.time}</span>}
                </div>
                <p className="truncate text-xs text-slate-500">
                  {job.client_name}{job.location ? ` · ${job.location}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {job.unstaffed && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
                    Nobody assigned
                  </span>
                )}
                {job.people.map((person) => (
                  <span key={person.staff_email}
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${CHIPS[person.status].className}`}
                        title={[
                          person.messaged_at ? 'Client messaged' : 'Client not messaged',
                          person.arrived_at ? 'Arrived' : 'Not arrived',
                          person.done_at ? 'All done' : 'Not finished',
                        ].join(' · ')}
                        data-testid={`today-person-${job.job_id}-${person.staff_email}`}>
                    {person.staff_name.split(' ')[0]}: {CHIPS[person.status].label}
                  </span>
                ))}
              </div>
            </div>

            {job.people.some((person) => person.problem_note) && (
              <div className="mt-2 rounded-[8px] border border-red-200 bg-red-50 p-2">
                {job.people.filter((p) => p.problem_note).map((person) => (
                  <p key={person.staff_email} className="text-xs text-red-700">
                    <AlertTriangle className="mr-1 inline h-3 w-3" />
                    <strong>{person.staff_name.split(' ')[0]}:</strong> {person.problem_note}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}

        {data?.needs_checking?.length > 0 && (
          <div className="rounded-[10px] border border-gold bg-cream p-3" data-testid="needs-checking">
            <p className="bd-heading mb-2 text-[11px] text-gold-deep">Needs checking</p>
            {data.needs_checking.map((job) => (
              <div key={job.job_id}
                   className="flex flex-wrap items-center justify-between gap-2 border-b border-hair py-2 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">
                    {job.job_type} · {ukDate(job.date)}
                  </p>
                  <p className="text-xs text-slate-600">{job.client_name} — {job.reason}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => clearChecking(job.job_id)}
                        data-testid={`clear-checking-${job.job_id}`}>
                  <Check className="mr-1 h-4 w-4" /> Checked
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TodayPanel;
